// Dependencies
import CheckBox from 'components/atoms/CheckBox';
import Icon from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import StarBox from 'components/atoms/StarBox';
import TypeTag from 'components/atoms/TypeTag';
import { DEFAULT_GRID_SETTINGS, MAX_TAGS, OUTLINE_INTERVALS, PADGE_LIMIT } from 'data/static';
import he from 'he';
import { Component, ReactNode } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { renderToString } from 'react-dom/server';
import { connect } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import { bindActionCreators, Dispatch } from 'redux';
import {
    SetErrorHandlerAction,
    setErrorHandlerAction,
    ShowMessageAction,
    showMessageAction,
} from 'store/actions/errorsActions';
import {
    SetGridControlsAction,
    setGridControlsAction,
    SetGridSettingsAction,
    setGridSettingsAction,
    SetLayoutAction,
    setLayoutAction,
} from 'store/actions/layoutActions';
import { twMerge } from 'tailwind-merge';
import {
    DataDisplayType,
    EnviroDisplayType,
    Layout,
    OpenModal,
    PortType,
    RootState,
    SwitchType,
    TagType,
    TransitType,
    UserType,
} from 'types';
import {
    GridColumnInterface,
    GridSettingsInterface,
    MainControlsInterface,
    MapControlsInterface,
} from 'types/functions';
import { TransitTag } from 'types/system';
import { checkDifferent, checkLayoutDifferent, clone } from 'utils/formHandlers';
import { getFlagImage, getFlagName } from 'utils/getVesselInfo';
import { hasPermission } from 'utils/permission';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

interface GridComponentProps {
    active: boolean;
    openMenu?: boolean;
    layout: Layout;
    transits: TransitType[];
    port: PortType;
    mainControls: MainControlsInterface;
    mapControls: MapControlsInterface;
    //unused props
    setGridControlsAction: SetGridControlsAction;

    setGridSettingsAction: SetGridSettingsAction;
    //unused props
    setErrorHandlerAction: SetErrorHandlerAction;
    showMessageAction: ShowMessageAction;
    openModelFunction: OpenModal;
    user: UserType;
    gridSettings: GridSettingsInterface;
    switchs: SwitchType;
    setLayoutAction: SetLayoutAction;
    tags: TagType[];
}

interface GridComponentState {
    active: boolean;
    layout: Layout;
    openMenu?: boolean;
    transitId: number;
    transits: TransitType[];
    page: number;
    searchId: string;
    searching: boolean;
    order: {
        uuid: string;
        type: string;
        order: string;
        column?: GridColumnInterface;
    };
    dataDisplay: DataDisplayType;
    gridSettings: GridSettingsInterface;
    switchs: SwitchType;
    filter: object;
    enviroDisplay: EnviroDisplayType;
    lastSelectedIndex: number | null;
    gridSize: number;
    tags: TagType[];
}

class Grid extends Component<GridComponentProps, GridComponentState> {
    constructor(props: GridComponentProps) {
        super(props);
        this.state = {
            tags: [],
            layout: clone(props.layout),
            active: false,
            searchId: props.layout.searchId,
            searching: props.layout.searching,
            openMenu: props.layout.openMenu,

            dataDisplay: props.port.meta_data.dataDisplay,
            enviroDisplay: props.port.meta_data.enviroDisplay,
            switchs: clone(props.switchs),
            gridSettings: clone(props.gridSettings),
            transitId: 0,
            transits: [],
            page: 1,
            lastSelectedIndex: null,
            filter: {},
            order: {
                uuid: DEFAULT_GRID_SETTINGS.columns[0].uuid,
                type: DEFAULT_GRID_SETTINGS.columns[0].key,
                order: DEFAULT_GRID_SETTINGS.columns[0].orderDirection,
                column: DEFAULT_GRID_SETTINGS.columns[0],
            },
            gridSize: props.gridSettings.columns
                .filter((x) => {
                    return x.status;
                })
                .reduce((sum, item) => sum + item.size, 0),
        };
    }

    static getDerivedStateFromProps(props: GridComponentProps, state: GridComponentState) {
        if (checkDifferent(props.gridSettings, state.gridSettings)) {
            return {
                gridSettings: clone(props.gridSettings),
                gridSize: props.gridSettings.columns
                    .filter((x) => {
                        return x.status;
                    })
                    .reduce((sum, item) => sum + item.size, 0),
            };
        }
        if (checkDifferent(props.tags, state.tags)) {
            return {
                tags: clone(props.tags),
            };
        }
        if (checkDifferent(props.switchs, state.switchs)) {
            return {
                switchs: clone(props.switchs),
            };
        }
        if (
            checkDifferent(props.transits, state.transits) ||
            checkLayoutDifferent(props.layout, state.layout, ['sections.grid', 'searchId', 'openMenu', 'searching']) ||
            props.active !== state.active
        ) {
            return {
                layout: clone(props.layout),
                openMenu: props.layout.openMenu,
                transits: clone(props.transits),
                // if search is changed reset the page
                page: props.layout.searchId !== state.searchId ? 1 : state.page,
                // if search is changed reset the order
                order:
                    props.layout.searchId !== state.searchId
                        ? {
                              uuid: DEFAULT_GRID_SETTINGS.columns[0].uuid,
                              type: DEFAULT_GRID_SETTINGS.columns[0].key,
                              order: DEFAULT_GRID_SETTINGS.columns[0].orderDirection,
                              column: DEFAULT_GRID_SETTINGS.columns[0],
                          }
                        : state.order,
                active: props.active,
                searchId: props.layout.searchId,
                searching: props.layout.searching,
            };
        }

        return null;
    }

    componentDidUpdate(
        prevProps: Readonly<GridComponentProps>,
        prevState: Readonly<GridComponentState>,
        snapshot?: any
    ): void {
        if (
            checkDifferent(
                prevState.transits.map((x) => x.id),
                this.state.transits.map((x) => x.id)
            ) &&
            Object.keys(this.state.filter).length > 0
        ) {
            setTimeout(() => {
                this.setFilter(clone(this.state.filter));
            }, 500);
        }
    }

    renderTags = (item: TransitType, toolTipId: string) => {
        let displayedCount = 0;
        const tags: JSX.Element[] = [];

        let usedSpace = this.state.gridSettings.columns.filter((x) => {
            return x.key === 'regionArea';
        }).length;

        if (
            this.state.gridSettings.columns.find((x) => {
                return x.key === 'end_utc' && x.status;
            })
        ) {
            usedSpace = usedSpace + 1;
        }

        usedSpace =
            usedSpace -
            this.state.gridSettings.columns.filter((x) => {
                return (x.key === 'direction' || x.key === 'vessel.name' || x.key === 'start_utc') && !x.status;
            }).length;

        // const DISPLAY_MAX_TAGS = MAX_TAGS - usedSpace;
        const tooltipTags: string[] = [];
        const DISPLAY_MAX_TAGS = MAX_TAGS - usedSpace;
        const addTag = (transitTag: TransitTag, keyPrefix: string, index: number) => {
            if (displayedCount < DISPLAY_MAX_TAGS) {
                displayedCount++;
                tags.push(
                    <TypeTag
                        className={twclsx(
                            transitTag.tag.type,
                            'whitespace-nowrap',
                            'mt-1',
                            'first-letter-uppercase',
                            'mr-2'
                        )}
                        key={`${keyPrefix}-${index}`}
                        type={transitTag.tag.type}
                    >
                        {transitTag.tag.type === 'system' || transitTag.tag.type === 'admin'
                            ? translate(transitTag.tag.label)
                            : transitTag.tag.label}
                    </TypeTag>
                );
            } else {
                const decoded = he.decode(
                    transitTag.tag.type === 'system' || transitTag.tag.type === 'admin'
                        ? translate(transitTag.tag.label)
                        : transitTag.tag.label
                );
                tooltipTags.push(decoded);
            }
        };

        const processTags = (
            filterFn: (tag: TransitTag) => boolean,
            sortFn: (a: TransitTag, b: TransitTag) => number,
            keyPrefix: string
        ) => {
            item.transit_tags
                .map((x) => {
                    return {
                        ...x,
                        tag:
                            this.props.tags.find((y) => {
                                return y.id === x.tag.id;
                            }) || {},
                    };
                })
                .filter(filterFn)
                .sort(sortFn)
                .forEach((transitTag, index) => addTag(transitTag, keyPrefix, index));
        };

        // Process each type of tag

        if (this.state.gridSettings.tags.includes('positioning')) {
            processTags(
                (transitTag) => transitTag.tag.type === 'positioning',
                (a, b) => {
                    if (a.tag?.order == null && b.tag?.order == null) return 0;
                    if (a.tag?.order == null) return 1;
                    if (b.tag?.order == null) return -1;
                    return a.tag.order - b.tag.order;
                },
                'positioning'
            );
        }

        if (this.state.gridSettings.tags.includes('analysis')) {
            processTags(
                (transitTag) => transitTag.tag.type === 'analysis',
                (a, b) => {
                    if (a.tag?.order == null && b.tag?.order == null) return 0;
                    if (a.tag?.order == null) return 1;
                    if (b.tag?.order == null) return -1;
                    return a.tag.order - b.tag.order;
                },
                'analysis'
            );
        }

        if (this.state.gridSettings.tags.includes('system')) {
            processTags(
                (transitTag) => ['admin', 'system'].includes(transitTag.tag.type),
                (a, b) => {
                    if (a.tag.id === 40 && b.tag.id !== 40) return -1;
                    if (b.tag.id === 40 && a.tag.id !== 40) return 1;

                    if (a.tag?.order == null && b.tag?.order == null) return 0;
                    if (a.tag?.order == null) return 1;
                    if (b.tag?.order == null) return -1;

                    return a.tag.order - b.tag.order;
                },
                'system'
            );
        }

        // TODO later to filter private tags with user id
        if (this.state.gridSettings.tags.includes('user')) {
            processTags(
                (transitTag) => ['public', 'private'].includes(transitTag.tag.type),
                (a, b) => {
                    if (a.tag?.order == null && b.tag?.order == null) return 0;
                    if (a.tag?.order == null) return 1;
                    if (b.tag?.order == null) return -1;
                    return a.tag.order - b.tag.order;
                },
                'user'
            );
        }

        let toolTipContent: any[] = [];
        // Add the "+ more" tooltip if there are extra tags
        if (tooltipTags.length >= 1) {
            toolTipContent = tooltipTags.map((x, i) => {
                return (
                    <div
                        key={i}
                        className={twMerge(
                            'first-letter-uppercase',
                            'whitespace-nowrap',
                            'text-sm',
                            'capitalize',
                            'px-2',
                            i !== tooltipTags.length - 1 && 'border-r',
                            'h-5',
                            'border-gray-300'
                        )}
                    >
                        {x}
                    </div>
                );
            });
            tags.unshift(
                <div key={`more-0`}>
                    <span
                        data-tooltip-id={toolTipId}
                        className="moretags px-2 py-1 text-sm rounded-full items-center cursor-pointer inline-flex font-medium leading-4 hide-border   whitespace-nowrap capitalize mr-2"
                    >
                        +{tooltipTags.length}
                    </span>
                </div>
            );
        }

        return { tagsContent: tags, toolTipContent: toolTipContent };
    };

    renderPages = () => {
        const elements: ReactNode[] = [];

        const page = this.state.page;
        const pages = Math.ceil(this.state.transits.length / PADGE_LIMIT);
        const from = page - 3 > 1 ? page - 3 : 1;
        const to = page + 3 < pages ? page + 3 : pages;

        if (page > 3) {
            elements.push(
                <span
                    key={0}
                    className="-mt-px pb-3 border-t-2 border-transparent pt-4 px-2 inline-flex items-center   leading-5 font-medium text-gray-700"
                >
                    ...
                </span>
            );
        }

        for (let index = from; index <= to; index++) {
            if (index === page) {
                elements.push(
                    <div
                        key={index}
                        className="-mt-px border-b-2 pb-3 border-blue-500 pt-4 px-2 inline-flex items-center   leading-5 font-medium text-blue-600 focus:outline-none focus:text-blue-800 focus:border-blue-700 transition ease-in-out duration-150"
                    >
                        {index}
                    </div>
                );
            } else {
                elements.push(
                    <div
                        key={index}
                        onClick={() => this.changepage(index)}
                        className="cursor-pointer pb-3 -mt-px border-b-2 border-transparent pt-4 px-2 inline-flex items-center  leading-5 font-medium text-gray-700 hover:text-gray-700 hover:border-gray-400 focus:outline-none focus:text-gray-700 focus:border-gray-500 transition ease-in-out duration-150"
                    >
                        {index}
                    </div>
                );
            }
        }

        if (page - 3 < pages && pages > 3) {
            elements.push(
                <span
                    key={pages + 1}
                    className="-mt-px pb-3 border-t-2 border-transparent pt-4 px-2 inline-flex items-center  leading-5 font-medium text-gray-700"
                >
                    ...
                </span>
            );
        }

        return elements;
    };

    changepage = (page: number) => {
        this.setState({
            page,
        });
    };

    renderTransits = (): TransitType[] => {
        const { transits, page, order } = this.state;

        const sortedTransits = [...transits]
            .map((x) => {
                //@ts-ignore
                x.direction = x.transit_tags.find((y) => {
                    return y.tag.type === 'type';
                })?.tag.tag;
                //@ts-ignore
                return x;
            })
            // .filter((x) => {
            //     // Check for each key in the filter
            //     const check = Object.keys(this.state.filter).map((y) => {
            //         // Find the column corresponding to the key
            //         const column = this.state.gridSettings.columns.find((z) => z.uuid === y);

            //         // Get the value for the current column and item
            //         const value = this.getRegionValue(column, x, true);

            //         // Check if the value is between min and max
            //         const { min, max } = this.state.filter[y];
            //         return value >= min && value <= max;
            //     });

            //     // Return true if all checks pass
            //     return check.every((value) => value === true);
            // })
            .sort((a, b) => {
                const { type, order: sortOrder } = order;

                const getValue = (obj: TransitType, order: any): any => {
                    const value = order.type.split('.').reduce((acc, part) => acc && acc[part], obj);
                    if (order.column.orderType === 'date') {
                        return value?.toTimestamp?.() ?? NaN;
                    }
                    if (order.column.orderType === 'number') {
                        return parseFloat(value);
                    }
                    return value;
                };

                const valueA = type === 'regionArea' ? this.getRegionValue(order.column, a, true) : getValue(a, order);
                const valueB = type === 'regionArea' ? this.getRegionValue(order.column, b, true) : getValue(b, order);

                // Handle NaN explicitly
                const isNaA = Number.isNaN(valueA);
                const isNaB = Number.isNaN(valueB);

                return sortOrder === 'asc' ? (valueA > valueB || isNaB ? 1 : -1) : valueA < valueB || isNaA ? 1 : -1;
            });

        if (this.state.gridSettings.type === 'compact') {
            return sortedTransits;
        }

        const startIndex = (page - 1) * PADGE_LIMIT;
        const endIndex = startIndex + PADGE_LIMIT;

        // Return the paginated slice of the sorted array
        return sortedTransits.slice(startIndex, endIndex);
    };

    renderOrder = (column: GridColumnInterface) => {
        if (!column.order) {
            return;
        }
        if (column.uuid !== this.state.order.uuid) {
            return (
                <svg
                    onClick={() => this.addOrder(column)}
                    className="w-3 h-3 orderLocation  mr-1 cursor-pointer float-left order"
                    viewBox="0 0 1792 1792"
                >
                    <path
                        fill="#a0aec0"
                        d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45zm0-384q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z"
                    />
                </svg>
            );
        } else {
            switch (this.state.order.order) {
                case 'asc':
                    return (
                        <svg
                            onClick={() => this.addOrder(column)}
                            className="w-3 h-3 orderLocation mr-2 cursor-pointer float-left order"
                            viewBox="0 0 1792 1792"
                        >
                            <path d="M1408 704q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z" />
                        </svg>
                    );
                case 'desc':
                    return (
                        <svg
                            onClick={() => this.addOrder(column)}
                            className="w-3 h-3 orderLocation mr-2 cursor-pointer float-left order"
                            viewBox="0 0 1792 1792"
                        >
                            <path d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45z" />
                        </svg>
                    );
                default:
                    return (
                        <svg
                            onClick={() => this.addOrder(column)}
                            className="w-3 h-3 orderLocation mr-2 cursor-pointer float-left order"
                            viewBox="0 0 1792 1792"
                        >
                            <path
                                fill="#a0aec0"
                                d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45zm0-384q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z"
                            />
                        </svg>
                    );
            }
        }
    };

    addOrder = (column: GridColumnInterface) => {
        let temp = this.state.order;
        if (temp.uuid === column.uuid) {
            temp.order = temp.order == 'desc' ? 'asc' : 'desc';
        } else {
            temp = { uuid: column.uuid, type: column.key, order: column.orderDirection, column: column };
        }
        //save order to state
        this.setState({
            order: temp,
        });
    };

    isAllSelected = (): boolean => {
        const { transits } = this.state;
        if (transits.length === 0) {
            return false;
        }
        return transits.every((transit) => transit.options?.select === true);
    };

    isAllhighlightd = (): boolean => {
        const { transits } = this.state;
        if (transits.length === 0) {
            return false;
        }
        return transits.every((transit) => transit.options?.highlight === true);
    };

    isAllDisplayed = (): boolean => {
        const { transits } = this.state;
        if (transits.length === 0) {
            return false;
        }
        return transits.every((transit) => transit.options?.display === true);
    };

    addTagsAndComments = (tab: string, id: number) => {
        this.props.openModelFunction(
            'transitTagsAndCommentsModel',
            0,
            'medium',
            { tab: tab, id: id },
            {
                onCancel: () => {
                    this.props.openModelFunction('transitTagsAndCommentsModel');
                },
                onConfirm: () => {
                    //console.log('saved')
                },
            },
            true
        );
    };

    findColumns = (key: string) => {
        if (
            this.state.gridSettings.columns.find((x) => {
                return x.key === key && x.status;
            })
        ) {
            return true;
        }

        return false;
    };

    getDisplay = (type: string, key: string) => {
        const dataDisplay = this.state?.dataDisplay;

        if (dataDisplay?.[type]?.[key]) {
            return dataDisplay[type][key];
        }

        return {};
    };

    displayValue = (key, value) => {
        return `${parseFloat(
            //@ts-ignore
            value * this.getDisplay('transit', key).database2display_unit_factor
        ).toFixed(this.getDisplay('transit', key).decimal)} ${this.getDisplay('transit', key).unit}`;
    };

    formatValue = (value, unit, key, type) => {
        const display = this.state.enviroDisplay[key][type];

        if (value === '-') {
            return value;
        }
        if (unit !== display.unit) {
            value = value * display.database2display_unit_factor;
        }

        return `${value.toFixed(display.decimal)} ${display.unit}`;
    };

    getRegionValue = (column, transit: TransitType, native = false) => {
        const analysisData = transit.transit_analysis.find((x) => {
            if (column.meta_data.port_url_id) {
                return (
                    x.port_area_id === column.meta_data.port_area_id &&
                    x.port_url_id === column.meta_data.port_url_id &&
                    x.key === `${column.meta_data.value}_${column.meta_data.parameter}`
                );
            } else {
                return (
                    x.port_area_id === column.meta_data.port_area_id &&
                    x.key === `${column.meta_data.value}_${column.meta_data.option}`
                );
            }
        });
        if (analysisData) {
            if (native) {
                return parseFloat(analysisData.value);
            }
            if (column.meta_data.port_url_id) {
                return parseFloat(
                    this.formatValue(
                        analysisData.value,
                        analysisData.key,
                        column.meta_data.option,
                        column.meta_data.parameter
                    )
                );
            } else {
                return parseFloat(this.displayValue(column.meta_data.option, analysisData.value));
            }
        }
        return parseFloat('nan');
    };

    getMinMaxRegionValues = (column) => {
        const values: number[] = this.state.transits
            .map((transit: TransitType) => {
                const value = this.getRegionValue(column, transit, true);
                // Ensure the value is a valid number
                return isNaN(value) ? null : value;
            })
            .filter((val) => val !== null) as number[]; // Filter out invalid values

        if (values.length === 0) {
            return { min: parseFloat('nan'), max: parseFloat('nan') }; // Return parseFloat('nan') if no valid values
        }

        return {
            min: Math.min(...values),
            max: Math.max(...values),
        };
    };

    toggleFilter = (column: GridColumnInterface) => {
        const { min, max } = this.getMinMaxRegionValues(column);

        this.props.openModelFunction(
            'gridValueFilterModel',
            0,
            'medium',
            {
                filter: this.state.filter,
                column,
                min,
                max,
            },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('gridValueFilterModel');
                },
                onConfirm: this.setFilter,
            },
            true
        );
    };

    getDisplaySetting = (column) => {
        if (column.meta_data?.port_url_id) {
            return this.state.enviroDisplay[column.meta_data.option][column.meta_data.parameter];
        } else {
            return this.state.dataDisplay.transit[column.meta_data.option];
        }
    };

    setFilter = (filter) => {
        const { transits } = this.state;

        this.setState({ filter }, () => {
            this.props.mainControls.updateDisplayAll(
                transits
                    .filter((x) => {
                        const check = Object.keys(filter).map((uuid) => {
                            const column = this.state.gridSettings.columns.find((z) => z.uuid === uuid);
                            const display = this.getDisplaySetting(column); // contains decimal
                            const decimal = display.decimal || 0;
                            const factor = Math.pow(10, decimal);

                            const value = this.getRegionValue(column, x, true);
                            const { min, max } = filter[uuid];

                            const minR = Math.floor(min * factor) / factor;

                            // round max **up**
                            const maxR = Math.ceil(max * factor) / factor;

                            return value >= minR && value <= maxR;
                        });

                        return check.every(Boolean);
                    })
                    .map((x) => x.id),
                true
            );
        });
    };

    renderFilter = (column: GridColumnInterface) => {
        if (!this.state.transits.length) {
            return;
        }
        return (
            <div
                onClick={() => {
                    this.toggleFilter(column);
                }}
                className="cursor-pointer"
            >
                <Icon
                    className={twclsx(
                        'h-4',
                        'w-4',
                        Object.keys(this.state.filter).includes(column.uuid) ? 'text-primary-link' : 'text-gray-400'
                    )}
                >
                    <path d="M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227z" />
                </Icon>
            </div>
        );
    };

    renderRegionTitle = (column: GridColumnInterface) => {
        if (!this.state.gridSettings.use_short_titles) {
            return column.label;
        }
        const names = {
            heading_deg: translate('heading'),
            rot_deg_min: translate('rot'),
            crot_deg_min: translate('crot'),
            drift_angle_deg: translate('drift angle'),
            cog_deg: translate('cog'),
            sog_mps: translate('sog'),
            wind: translate('wind'),
            tide: translate('tide'),
            wave: translate('wave'),
            watercurrent: translate('water current'),
        };

        return names[column.meta_data.option];
    };

    handleSelectClick = async (
        transitId: number,
        transitIndex: number,
        isShift: boolean,
        isControl: boolean,
        value: boolean
    ) => {
        const transits = this.renderTransits(); // Assuming this returns an array of transit objects with `id`

        const ids: number[] = [];
        const values: boolean[] = [];

        if (!isControl && !isShift) {
            // Normal click: Select only the clicked transit, deselect all others
            transits.forEach((transit) => {
                ids.push(transit.id);
                values.push(transit.id === transitId ? !value : false);
            });
        } else if (isControl) {
            // Ctrl-click: Toggle selection without affecting others
            ids.push(transitId);
            values.push(!value);
        } else if (isShift && this.state.lastSelectedIndex !== null) {
            // Shift-click: Select range between last selected index and current
            const start = Math.min(this.state.lastSelectedIndex, transitIndex);
            const end = Math.max(this.state.lastSelectedIndex, transitIndex);

            transits.forEach((transit, index) => {
                if (index >= start && index <= end) {
                    ids.push(transit.id);
                    values.push(true);
                }
            });
        }

        // Update selections in bulk
        if (ids.length > 0) {
            await this.props.mainControls.updateSelectionFromArray(ids, values);
        }

        // Wait for selection updates, then update state
        this.setState({ lastSelectedIndex: transitIndex });
    };

    toggleTransitInfo = (transitId) => {
        this.setState((prevState) => ({
            transitId: prevState.transitId === transitId ? null : transitId,
        }));
    };

    saveGridColumn = (column: GridColumnInterface) => {
        const { gridSettings } = this.state;

        const existingColumnIndex = gridSettings.columns.findIndex((col) => col.uuid === column.uuid);

        // If column with the same uuid exists, update it
        if (existingColumnIndex !== -1) {
            const updatedColumns = [...gridSettings.columns];
            updatedColumns[existingColumnIndex] = column;
            this.props.setGridSettingsAction({
                ...gridSettings,
                columns: updatedColumns,
            });
        }

        // Otherwise, add the new column
        this.props.setGridSettingsAction({
            ...gridSettings,
            columns: [...gridSettings.columns, column],
        });
    };

    addGridColumn = () => {
        this.props.openModelFunction(
            'addGridColumnModel',
            0,
            'medium',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('addGridColumnModel');
                },
                onConfirm: this.saveGridColumn,
            },
            true
        );
    };

    updateTagSetting = (type) => {
        this.setState((prevState) => {
            const { gridSettings } = prevState;
            const updatedTags = gridSettings.tags.includes(type)
                ? gridSettings.tags.filter((tag) => tag !== type)
                : [...gridSettings.tags, type];

            this.props.setGridSettingsAction(
                {
                    ...gridSettings,
                    tags: updatedTags,
                },
                false
            );
        });
    };

    inspectTransit = async (transit: TransitType) => {
        this.props.mapControls.chart?.hideTooltips();
        await this.props.mapControls.chart?.setClickMatrix([
            {
                selected: true,
                transitId: transit.id,
                positionId: null,
                skipInterval: OUTLINE_INTERVALS.map(() => {
                    return 0;
                }),
                index: 0,
                min: 0,
                max: null,
            },
        ]);
        await this.props.mainControls.inspectTransit(transit.id);
    };

    render() {
        return (
            <div
                className={twclsx(
                    !this.state.active && 'hidden',
                    'mt-14',
                    'h-page',
                    'border-r',
                    'border-gray-300',
                    'padding-transition',
                    'fixed',
                    'z-10',
                    'left-0',
                    'right-0',
                    'margin-transition',
                    this.state.openMenu ? 'xl:ml-menu md:ml-14 xl:w-main md:w-fullPage' : 'ml-14 w-fullPage'
                )}
            >
                {this.state.searching ? (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                        <div className="flex items-center justify-center">
                            <Image alt="loading" className="mx-2 w-5" src="/static/images/loading.svg" />
                            {translate('Loading search data...')}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid   h-8 grid-cols-24 border-b border-gray-300 relative raw">
                            <div className="col-span-2 flex items-center">
                                <div className="px-2 flex justify-between ">
                                    <div
                                        className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                        data-tooltip-id="selectAllGridHeader"
                                    >
                                        <CheckBox name="0" value={this.isAllSelected()} />
                                    </div>

                                    <div
                                        className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                        data-tooltip-id="highlightAllGridHeader"
                                    >
                                        <StarBox name="0" value={this.isAllhighlightd()} />
                                    </div>
                                    <div className="relative   cursor-pointer text-left  -mr-px  inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                        <Icon className="w-4 h-4">
                                            <path d="M7.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                            <path d="M3 6v5.172a2 2 0 0 0 .586 1.414l7.71 7.71a2.41 2.41 0 0 0 3.408 0l5.592 -5.592a2.41 2.41 0 0 0 0 -3.408l-7.71 -7.71a2 2 0 0 0 -1.414 -.586h-5.172a3 3 0 0 0 -3 3z"></path>
                                        </Icon>
                                    </div>
                                </div>
                                <div className=" rawActionsLeft no-select flex whitespace-no-wrap text-right h-full left-0 pr-10 pl-2 leading-5 font-medium">
                                    <div
                                        className="relative cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                        data-tooltip-id="selectAllGridHeader"
                                    >
                                        <CheckBox
                                            className="text-white"
                                            name="0"
                                            onChange={this.props.mainControls.updateDisplay}
                                            value={this.isAllSelected()}
                                        />
                                        <Tooltip
                                            className="z-100"
                                            id="selectAllGridHeader"
                                            html={`${translate('Select all transits')}`}
                                            place={'bottom-start'}
                                        />
                                    </div>

                                    <div
                                        className="relative cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                        data-tooltip-id="highlightAllGridHeader"
                                    >
                                        <StarBox
                                            className="text-white"
                                            name="0"
                                            onChange={(o) => {
                                                this.props.mainControls.updatehighlight(o, !this.isAllhighlightd());
                                            }}
                                            value={this.isAllhighlightd()}
                                        />

                                        <Tooltip
                                            className="z-100"
                                            id="highlightAllGridHeader"
                                            html={`${translate('highlight transits')}`}
                                            place={'bottom-start'}
                                        />
                                    </div>
                                    <div
                                        onClick={() => {
                                            const { transits } = this.props.mainControls.getTransit(0);
                                            if (
                                                !transits.filter((x) => {
                                                    return x.options.select;
                                                }).length
                                            ) {
                                                this.props.showMessageAction({
                                                    messageType: 'WARN',
                                                    message: translate('No transits selected.'),
                                                });

                                                return false;
                                            }

                                            this.props.openModelFunction(
                                                'allTransitTagsAndCommentsModel',
                                                0,
                                                'medium',
                                                {},
                                                {
                                                    onCancel: () => {
                                                        this.props.openModelFunction('allTransitTagsAndCommentsModel');
                                                    },
                                                    onConfirm: () => {
                                                        //console.log('saved');
                                                    },
                                                },
                                                true
                                            );
                                        }}
                                        data-tooltip-id="gridTagsAndComments"
                                        className="relative   cursor-pointer text-left  -mr-px  inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                    >
                                        <Tooltip
                                            id="gridTagsAndComments"
                                            html={`${translate('Tags and comments')}`}
                                            place="bottom-start"
                                            className="z-40"
                                        />
                                        <Icon className="w-4 h-4 text-white">
                                            <path d="M7.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                            <path d="M3 6v5.172a2 2 0 0 0 .586 1.414l7.71 7.71a2.41 2.41 0 0 0 3.408 0l5.592 -5.592a2.41 2.41 0 0 0 0 -3.408l-7.71 -7.71a2 2 0 0 0 -1.414 -.586h-5.172a3 3 0 0 0 -3 3z"></path>
                                        </Icon>
                                    </div>
                                </div>
                            </div>
                            {this.state.gridSettings.columns
                                .filter((x) => {
                                    return x.status;
                                })
                                .map((x, i) => {
                                    let cutTags = 0;
                                    if (x.key === 'tags') {
                                        cutTags = 22 - this.state.gridSize;
                                    }

                                    return (
                                        <>
                                            <div
                                                key={i}
                                                className={twclsx(
                                                    'py-1',
                                                    'capitalize',
                                                    `col-span-${x.size + cutTags}`,
                                                    'flex',
                                                    'items-center',
                                                    'truncate ',
                                                    'whitespace-nowrap'
                                                )}
                                                data-tooltip-id={`column_${x.uuid}`}
                                            >
                                                <div> {x.key === 'regionArea' && this.renderFilter(x)}</div>
                                                <div> {this.renderOrder(x)} </div>
                                                {x.key === 'regionArea' ? (
                                                    <div className="w-full truncate"> {this.renderRegionTitle(x)} </div>
                                                ) : (
                                                    <div> {translate(x.label)}</div>
                                                )}

                                                {x.key === 'regionArea' && (
                                                    <Tooltip
                                                        className="z-20"
                                                        id={`column_${x.uuid}`}
                                                        html={x.label}
                                                        place={'bottom'}
                                                    />
                                                )}

                                                {x.key === 'tags' && (
                                                    <div className="flex gap-2 ml-2">
                                                        <div
                                                            onClick={() => {
                                                                this.updateTagSetting('user');
                                                            }}
                                                        >
                                                            <TypeTag
                                                                active={this.state.gridSettings.tags.includes('user')}
                                                                type="user"
                                                            >
                                                                {translate('User')}
                                                            </TypeTag>
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                this.updateTagSetting('analysis');
                                                            }}
                                                        >
                                                            <TypeTag
                                                                active={this.state.gridSettings.tags.includes(
                                                                    'analysis'
                                                                )}
                                                                type="analysis"
                                                            >
                                                                {translate('Analysis')}
                                                            </TypeTag>
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                this.updateTagSetting('positioning');
                                                            }}
                                                        >
                                                            <TypeTag
                                                                active={this.state.gridSettings.tags.includes(
                                                                    'positioning'
                                                                )}
                                                                type="positioning"
                                                            >
                                                                {translate('Route')}
                                                            </TypeTag>
                                                        </div>
                                                        <div
                                                            onClick={() => {
                                                                this.updateTagSetting('system');
                                                            }}
                                                        >
                                                            <TypeTag
                                                                active={this.state.gridSettings.tags.includes('system')}
                                                                type="system"
                                                            >
                                                                {translate('System')}
                                                            </TypeTag>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    );
                                })}
                            <div
                                data-tooltip-id={`addGridColumn`}
                                onClick={this.addGridColumn}
                                className="absolute bg-primary-system rounded-sm bg-opacity-50 right-1 my-1 py-1 px-1 cursor-pointer"
                            >
                                <Icon className="w-4 h-4 text-white">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M12 5l0 14" />
                                    <path d="M5 12l14 0" />
                                </Icon>
                                <Tooltip
                                    className="z-20"
                                    id={`addGridColumn`}
                                    html={translate('Add custom column')}
                                    place={'bottom-end'}
                                />
                            </div>
                        </div>

                        <Scrollbars
                            renderThumbHorizontal={() => <div style={{ display: 'none' }} />}
                            renderTrackHorizontal={() => <div style={{ display: 'none' }} />}
                            style={{
                                height: 'calc( 100vh - 142px)',
                            }}
                        >
                            {this.renderTransits().map((transit, transitIndex) => {
                                const { tagsContent, toolTipContent } = this.renderTags(
                                    transit,
                                    `tagToolTip-${transit.id}`
                                );

                                return (
                                    <>
                                        <div
                                            key={transit.uuid}
                                            className={twclsx(
                                                //BACK UI-2.1
                                                //  transit.options.select && 'bg-gray-200',
                                                transit.options.inspect && 'bg-blue-100',
                                                'grid',
                                                'raw',
                                                'grid-cols-24',
                                                'border-b',
                                                'border-gray-300',
                                                'hover:bg-gray-100',
                                                'relative'
                                            )}
                                        >
                                            <div className="py-1 col-span-2 flex items-center">
                                                <div className="px-2 flex justify-between  ">
                                                    <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                        <CheckBox
                                                            name={transit.id}
                                                            onChange={this.props.mainControls.updateDisplay}
                                                            value={transit.options.display}
                                                        />
                                                    </div>
                                                    {/* <div
                                                data-tooltip-id={`gridHideOrShowThisTransit-${transit.id}`}
                                                className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                            >
                                                <Tooltip
                                                    id={`gridHideOrShowThisTransit-${transit.id}`}
                                                    html={`${
                                                        transit.options.display
                                                            ? translate('Hide this transit')
                                                            : translate('Show this transit')
                                                    }`}
                                                    place="left"
                                                />

                                                <DisplayBox
                                                    name={transit.id}
                                                    onChange={this.props.mainControls.updateDisplay}
                                                    value={transit.options.display}
                                                />
                                            </div> */}

                                                    <div className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                        <StarBox
                                                            name={transit.id}
                                                            onChange={this.props.mainControls.updatehighlight}
                                                            value={transit.options.highlight}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {this.findColumns('start_utc') && (
                                                <div
                                                    //BACK 2.1
                                                    // onClick={(event) => {
                                                    //     // this.handleSelectClick(
                                                    //     //     transit.id,
                                                    //     //     transitIndex,
                                                    //     //     event.shiftKey,
                                                    //     //     event.ctrlKey,
                                                    //     //     transit.options.select
                                                    //     // );
                                                    // }}
                                                    //REMOVE 2.1
                                                    onClick={async () => {
                                                        this.inspectTransit(transit);
                                                    }}
                                                    className=" py-1 no-select col-span-2 cursor-pointer"
                                                >
                                                    <div
                                                        data-tooltip-id={`startDateTooltip ${transit.id}`}
                                                        className={twclsx(
                                                            (!hasPermission(this.props.user, this.props.port.id, [
                                                                'identifyTransits',
                                                            ]) ||
                                                                !this.state.switchs.identifyTransits.value) &&
                                                                'blurText',
                                                            'text-base'
                                                        )}
                                                    >
                                                        {transit.start_utc.formatDateTimeToString('dd LLL yyyy')}
                                                    </div>
                                                    {this.state.gridSettings.type === 'default' ? (
                                                        <div className="text-sm">
                                                            {transit.start_utc.formatDateTimeToString('HH:mm:ss Z')}
                                                        </div>
                                                    ) : (
                                                        <Tooltip
                                                            className="z-20"
                                                            id={`startDateTooltip ${transit.id}`}
                                                            html={`${transit.start_utc.formatDateTimeToString(
                                                                'dd LLL yyyy'
                                                            )} ${transit.start_utc.formatDateTimeToString(
                                                                'HH:mm:ss Z'
                                                            )}`}
                                                            place={'top-start'}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {this.findColumns('end_utc') && (
                                                <div
                                                    //BACK 2.1
                                                    // onClick={(event) => {
                                                    //     this.handleSelectClick(
                                                    //         transit.id,
                                                    //         transitIndex,
                                                    //         event.shiftKey,
                                                    //         event.ctrlKey,
                                                    //         transit.options.select
                                                    //     );
                                                    // }}
                                                    //REMOVE 2.1
                                                    onClick={async () => {
                                                        this.inspectTransit(transit);
                                                    }}
                                                    className=" py-1 no-select col-span-2 cursor-pointer"
                                                >
                                                    <div
                                                        data-tooltip-id={`endDateTooltip ${transit.id}`}
                                                        className={twclsx(
                                                            (!hasPermission(this.props.user, this.props.port.id, [
                                                                'identifyTransits',
                                                            ]) ||
                                                                !this.state.switchs.identifyTransits.value) &&
                                                                'blurText',
                                                            'text-base'
                                                        )}
                                                    >
                                                        {transit.end_utc.formatDateTimeToString('dd LLL yyyy')}
                                                    </div>
                                                    {this.state.gridSettings.type === 'default' ? (
                                                        <div className="text-sm">
                                                            {transit.end_utc.formatDateTimeToString('HH:mm:ss Z')}
                                                        </div>
                                                    ) : (
                                                        <Tooltip
                                                            className="z-20"
                                                            id={`endDateTooltip ${transit.id}`}
                                                            html={`${transit.end_utc.formatDateTimeToString(
                                                                'dd LLL yyyy'
                                                            )} ${transit.end_utc.formatDateTimeToString('HH:mm:ss Z')}`}
                                                            place={'top-start'}
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {this.findColumns('direction') && (
                                                <div
                                                    onClick={async () => {
                                                        this.inspectTransit(transit);
                                                    }}
                                                    className=" py-1 no-select col-span-2 flex cursor-pointer items-center"
                                                >
                                                    {transit.transit_tags
                                                        .filter((transitTag) => {
                                                            return transitTag.tag.type == 'type';
                                                        })
                                                        .sort((a, b) => b.id - a.id)
                                                        .reverse()
                                                        .map((transitTag, index) => {
                                                            if (index < 3) {
                                                                return (
                                                                    <TypeTag
                                                                        className="first-letter-uppercase"
                                                                        key={index}
                                                                        type={transitTag.tag.type}
                                                                    >
                                                                        {translate(transitTag.tag.label)}
                                                                    </TypeTag>
                                                                );
                                                            } else {
                                                                return null;
                                                            }
                                                        })}
                                                </div>
                                            )}
                                            {this.findColumns('vessel.name') && (
                                                <div
                                                    onClick={async () => {
                                                        this.inspectTransit(transit);
                                                    }}
                                                    className=" py-1 col-span-4 cursor-pointer"
                                                >
                                                    <div className="flex items-center justify-between mr-2">
                                                        <div className="flex justify-start gap-1">
                                                            <div className="pt-1 md:hidden lg:block block">
                                                                <Image
                                                                    alt={getFlagName(transit?.mmsi) || ''}
                                                                    src={getFlagImage(transit.mmsi) || ''}
                                                                />
                                                            </div>
                                                            <div
                                                                data-tooltip-id={`vesselNameTooltip ${transit.id}`}
                                                                className={twclsx(
                                                                    (!hasPermission(
                                                                        this.props.user,
                                                                        this.props.port.id,
                                                                        ['identifyTransits']
                                                                    ) ||
                                                                        !this.state.switchs.identifyTransits.value) &&
                                                                        'blurText',
                                                                    'text-base',
                                                                    'truncate ',
                                                                    'xl:w-auto',
                                                                    'w-30',
                                                                    'md:w-20'
                                                                )}
                                                            >
                                                                {transit.vessel.name}
                                                            </div>
                                                        </div>

                                                        {/* <div className=" relative cursor-pointer sm:hidden  text-left inline-flex lg:inline-flex items-left justify-start items-center leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300">
                                                    <div
                                                        onClick={async () => {
                                                            // await this.props.mainControls.expandTransit(transit.id);
                                                            this.toggleTransitInfo(transit.id);
                                                        }}
                                                        data-tooltip-id="transitlistExpandTransit"
                                                        className="  col-span-1 relative   cursor-pointer text-left    inline-flex items-left justify-center items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                    >
                                                        <Tooltip
                                                            id="transitlistExpandTransit"
                                                            html={`${translate('Show/Hide transit info')}`}
                                                            place="left"
                                                        />
                                                        <Icon className="w-5  h-5">
                                                            <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                                                            <path d="M12 9h.01" />
                                                            <path d="M11 12h1v4h1" />
                                                        </Icon>
                                                        {this.state.transitId === transit.id && (
                                                            <InspectTransitTab
                                                                transitId={transit.id}
                                                                toggleTransitInfo={() =>
                                                                    this.toggleTransitInfo(transit.id)
                                                                }
                                                                section="grid"
                                                            ></InspectTransitTab>
                                                        )}
                                                    </div>
                                                    <div
                                                        onClick={() => {
                                                            this.props.mainControls.playbackTransit(
                                                                transit.id,
                                                                transit.start_utc
                                                            );
                                                        }}
                                                        data-tooltip-id="gridReplayThisTransit"
                                                        className="relative cursor-pointer sm:hidden  text-left inline-flex lg:inline-flex items-left justify-start items-center leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                    >
                                                        <Tooltip
                                                            id="gridReplayThisTransit"
                                                            html={`${translate('Replay this transit')}`}
                                                            place="left"
                                                        />
                                                        <Icon currentColor={SYSTEM_COLOR} className="w-4  h-4">
                                                            <path d="M4 12v-3a3 3 0 0 1 3 -3h13m-3 -3l3 3l-3 3"></path>
                                                            <path d="M20 12v3a3 3 0 0 1 -3 3h-13m3 3l-3 -3l3 -3"></path>
                                                        </Icon>
                                                    </div>
                                                </div> */}
                                                    </div>
                                                    {this.state.gridSettings.type === 'default' ? (
                                                        <div>
                                                            <span
                                                                className={twclsx(
                                                                    (!hasPermission(
                                                                        this.props.user,
                                                                        this.props.port.id,
                                                                        ['identifyTransits']
                                                                    ) ||
                                                                        !this.state.switchs.identifyTransits.value) &&
                                                                        'blurText',
                                                                    'text-base',
                                                                    'mr-1'
                                                                )}
                                                            >
                                                                {transit.mmsi}
                                                            </span>
                                                            <span className="capitalize no-select">
                                                                ({translate(transit.vessel.vessel_category.label)})
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <Tooltip
                                                            className="z-20"
                                                            id={`vesselNameTooltip ${transit.id}`}
                                                            html={`${transit.mmsi} ${transit.vessel.vessel_category.label}`}
                                                            place={'top-start'}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {this.findColumns('vessel.loa_m') && (
                                                <div className=" py-1 col-span-1 no-selecttext-base cursor-pointer flex items-center">
                                                    {transit.vessel.loa_m} m
                                                </div>
                                            )}
                                            {this.findColumns('vessel.beam_m') && (
                                                <div className=" py-1 col-span-1 cursor-pointer no-select text-base flex items-center">
                                                    {transit.vessel.beam_m} m
                                                </div>
                                            )}
                                            {this.findColumns('draft_m') && (
                                                <div className=" py-1 cursor-pointer col-span-1 no-select text-base flex items-center">
                                                    {transit.draft_m ? `${transit.draft_m.toFixed(2)} m` : 'N/A'}
                                                </div>
                                            )}
                                            {this.findColumns('comments') && (
                                                <div
                                                    onClick={() => {
                                                        this.addTagsAndComments('comments', transit.id);
                                                    }}
                                                    className=" py-1 col-span-2 no-select text-primary-link text-base flex items-center cursor-pointer underline decoration-solid" //
                                                >
                                                    {transit.comments.filter((x) => {
                                                        return (
                                                            (x.user_id === this.props.user.id &&
                                                                x.type === 'private') ||
                                                            x.type !== 'private'
                                                        );
                                                    }).length === 0 ? (
                                                        <Icon className="w-4 h-4 ">
                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                            <path d="M12 5l0 14" />
                                                            <path d="M5 12l14 0" />
                                                        </Icon>
                                                    ) : (
                                                        transit.comments.filter((x) => {
                                                            return (
                                                                (x.user_id === this.props.user.id &&
                                                                    x.type === 'private') ||
                                                                x.type !== 'private'
                                                            );
                                                        }).length
                                                    )}
                                                </div>
                                            )}
                                            {this.findColumns('tags') && (
                                                <div
                                                    onClick={() => {
                                                        this.addTagsAndComments('tags', transit.id);
                                                    }}
                                                    className={twclsx(
                                                        'py-1',
                                                        `col-span-${9 + (22 - this.state.gridSize)}`,
                                                        'items-center',
                                                        'no-select',
                                                        'flex',
                                                        'flex-wrap',
                                                        'cursor-pointer'
                                                    )}
                                                >
                                                    {tagsContent}
                                                    <Tooltip
                                                        style={{ zIndex: 100 }}
                                                        positionStrategy="fixed"
                                                        id={`tagToolTip-${transit.id}`}
                                                        html={renderToString(
                                                            <div className="first-letter-uppercase flex-wrap flex   max-w-96 z-100">
                                                                {toolTipContent}
                                                            </div>
                                                        )}
                                                        place="left-start"
                                                    />
                                                </div>
                                            )}
                                            {this.state.gridSettings.columns
                                                .filter((x) => {
                                                    return x.status && x.key === 'regionArea';
                                                })
                                                .map((x, i) => {
                                                    return (
                                                        <div
                                                            className=" py-1 col-span-2 text-base flex items-center"
                                                            key={i}
                                                        >
                                                            {this.getRegionValue(x, transit)}
                                                        </div>
                                                    );
                                                })}
                                            <div className=" rawActionsLeft no-select flex whitespace-no-wrap text-right h-full left-0 pr-10 pl-2 leading-5 font-medium">
                                                <div
                                                    data-tooltip-id="ShowOrHideTransit"
                                                    className="relative   cursor-pointer text-left   inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                >
                                                    <CheckBox
                                                        className="text-white"
                                                        name={transit.id}
                                                        onChange={this.props.mainControls.updateDisplay}
                                                        value={transit.options.display}
                                                    />
                                                    <Tooltip
                                                        id="ShowOrHideTransit"
                                                        html={`${translate('Show/hide transit')}`}
                                                        place={'bottom-start'}
                                                        className="z-40"
                                                    />
                                                </div>
                                                <div
                                                    data-tooltip-id="highlightTransit"
                                                    className="relative   cursor-pointer text-left   -mr-px  inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                >
                                                    <StarBox
                                                        className="text-white"
                                                        name={transit.id}
                                                        onChange={this.props.mainControls.updatehighlight}
                                                        value={transit.options.highlight}
                                                    />
                                                    <Tooltip
                                                        id="highlightTransit"
                                                        html={`${translate('Highlight transit')}`}
                                                        place={'bottom-start'}
                                                        className="z-40"
                                                    />
                                                </div>
                                                <div
                                                    onClick={() => {
                                                        this.addTagsAndComments('tags', transit.id);
                                                    }}
                                                    data-tooltip-id="gridTagsAndComments"
                                                    className="relative   cursor-pointer text-left  -mr-px  inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                >
                                                    <Tooltip
                                                        id="gridTagsAndComments"
                                                        html={`${translate('Tags and comments')}`}
                                                        place={'bottom-end'}
                                                        className="z-40"
                                                    />
                                                    <Icon className="w-4 h-4 text-white">
                                                        <path d="M7.5 7.5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
                                                        <path d="M3 6v5.172a2 2 0 0 0 .586 1.414l7.71 7.71a2.41 2.41 0 0 0 3.408 0l5.592 -5.592a2.41 2.41 0 0 0 0 -3.408l-7.71 -7.71a2 2 0 0 0 -1.414 -.586h-5.172a3 3 0 0 0 -3 3z"></path>
                                                    </Icon>
                                                </div>
                                                {this.props.mainControls.getDisplayedTransits().length > 1 ? (
                                                    <div
                                                        data-tooltip-id="gridShowOnlyThisTransit"
                                                        className="relative   cursor-pointer text-left -mr-px  inline-flex  items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                        onClick={async () => {
                                                            await this.props.mainControls.updateDisplayAll(
                                                                [transit.id],
                                                                true
                                                            );
                                                        }}
                                                    >
                                                        <Tooltip
                                                            id="gridShowOnlyThisTransit"
                                                            html={`${translate('Show only this transit')}`}
                                                            place={'bottom-end'}
                                                            className="z-40"
                                                        />
                                                        <Icon className="w-4 h-4 text-white">
                                                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                            <path d="M11.102 17.957c-3.204 -.307 -5.904 -2.294 -8.102 -5.957c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6a19.5 19.5 0 0 1 -.663 1.032"></path>

                                                            <path d="M18 21v-7l-2 2" />
                                                        </Icon>
                                                    </div>
                                                ) : (
                                                    <div
                                                        data-tooltip-id="gridShowOnlyThisTransit"
                                                        className="relative   cursor-pointer text-left   -mr-px  inline-flex  items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                        onClick={async () => {
                                                            await this.props.mainControls.updateDisplayAll(
                                                                [0],
                                                                false,
                                                                false
                                                            );
                                                        }}
                                                    >
                                                        <Tooltip
                                                            id="gridShowOnlyThisTransit"
                                                            html={`${translate('Show all transits')}`}
                                                            place={'bottom-end'}
                                                            className="z-40"
                                                        />
                                                        <Icon className="w-4 h-4 text-white">
                                                            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                                                            <path d="M11.102 17.957c-3.204 -.307 -5.904 -2.294 -8.102 -5.957c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6a19.5 19.5 0 0 1 -.663 1.032"></path>
                                                            <path d="M15 19l2 2l4 -4"></path>
                                                        </Icon>
                                                    </div>
                                                )}

                                                <div
                                                    onClick={() => {
                                                        this.props.mainControls.playbackTransit(
                                                            transit.id,
                                                            transit.start_utc
                                                        );
                                                    }}
                                                    data-tooltip-id="gridReplayThisTransit"
                                                    className="relative   cursor-pointer text-left  -mr-px  inline-flex items-left justify-start items-center py-1  leading-5 text-primary font-medium border border-transparent rounded-bl-lg hover:text-blue-800 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 transition ease-in-out duration-300"
                                                >
                                                    <Tooltip
                                                        id="gridReplayThisTransit"
                                                        html={`${translate('Replay this transit')}`}
                                                        place={'bottom-end'}
                                                        className="z-40"
                                                    />
                                                    <Icon className="w-4  h-4 text-white">
                                                        <path d="M4 12v-3a3 3 0 0 1 3 -3h13m-3 -3l3 3l-3 3"></path>
                                                        <path d="M20 12v3a3 3 0 0 1 -3 3h-13m3 3l-3 -3l3 -3"></path>
                                                    </Icon>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })}
                        </Scrollbars>

                        <div className="flex h-10 p-1 justify-between border-t border-gray-300  ">
                            {this.state.gridSettings.type === 'default' ? (
                                <>
                                    <div className="w-36 flex text-center ">{this.renderPages()}</div>
                                    <p className="  py-3 px-2 text-gray-700">
                                        {translate('pagination', {
                                            showingFrom: (this.state.page - 1) * PADGE_LIMIT + 1,
                                            showingTo:
                                                (this.state.page - 1) * PADGE_LIMIT + PADGE_LIMIT >
                                                this.state.transits.length
                                                    ? this.state.transits.length
                                                    : (this.state.page - 1) * PADGE_LIMIT + PADGE_LIMIT,
                                            of: this.state.transits.length,
                                        })}
                                    </p>
                                </>
                            ) : (
                                <div className="  py-1 px-2 text-gray-700">{translate('compact view')}</div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }
} 

const gridStateToProps = (state: RootState) => ({
    layout: state.layout.layout,
    port: state.port.port,
    mainControls: state.layout.mainControls,
    mapControls: state.map.mapControls,
    openModelFunction: state.layout.openModelFunction,
    user: state.user.user,
    switchs: state.layout.switchs,
    gridSettings: state.layout.gridSettings,
    tags: state.port.tags,
});

const gridDispatchToProps = (dispatch: Dispatch) => ({
    setGridControlsAction: bindActionCreators(setGridControlsAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setGridSettingsAction: bindActionCreators(setGridSettingsAction, dispatch),
    showMessageAction: bindActionCreators(showMessageAction, dispatch),

    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(gridStateToProps, gridDispatchToProps)(Grid);
